import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Login } from '@mui/icons-material';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, loading, login } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && currentUser) {
    return <Navigate to={currentUser.is_admin ? '/admin/projects' : '/projects'} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      const user = await login(email.trim(), password);
      navigate(user.is_admin ? '/admin/projects' : '/projects', { replace: true });
    } catch {
      setError(t('invalidEmailOrPassword'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 160px)', display: 'grid', placeItems: 'center' }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 420, p: 3 }}>
        <Stack component="form" spacing={2.5} onSubmit={handleSubmit}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={800} gutterBottom>
              Talegence
            </Typography>
            <Typography color="text.secondary">{t('signInSubtitle')}</Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('email')}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
            required
            autoComplete="email"
          />
          <TextField
            label={t('password')}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            fullWidth
            required
            autoComplete="current-password"
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Login />}
            disabled={submitting}
          >
            {t('signIn')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoginPage;
